/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, Outlet } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BusinessMediaGallery from "@/components/BusinessMediaGallery";
import BusinessSeo from "@/components/BusinessSeo";
import BusinessQualityLayer from "@/components/BusinessQualityLayer";
import BusinessReviewHub from "@/components/BusinessReviewHub";
import AccessibilityEnhancements from "@/components/AccessibilityEnhancements";
import ConceptLifecycleGate from "@/components/ConceptLifecycleGate";
import CustomerWebsiteAccessLayer from "@/components/CustomerWebsiteAccessLayer";
import RegionRouteSync from "@/components/RegionRouteSync";
import SupportBubble from "@/components/SupportBubble";
import { initMonitoring } from "@/lib/monitoring";
import { API, backendConfigured } from "@/lib/api";
import AdSenseScriptLoader from "@/components/ads/AdSenseScriptLoader";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Plans from "@/pages/Plans";
import PlanDetail from "@/pages/PlanDetail";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import WhyThisMatters from "@/pages/WhyThisMatters";
import ConceptCorrectionRequest from "@/pages/ConceptCorrectionRequest";
import AdminContacts from "@/pages/AdminContacts";
import AdminReviews from "@/pages/AdminReviews";
import AdminLogin from "@/pages/AdminLogin";
import Register from "@/pages/Register";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSites from "@/pages/AdminSites";
import AdminSiteDetail from "@/pages/AdminSiteDetail";
import AdminSales from "@/pages/AdminSales";
import AdminSalesDetail from "@/pages/AdminSalesDetail";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminFinance from "@/pages/AdminFinance";
import AdminCompliance from "@/pages/AdminCompliance";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import { CustomerAuthProvider } from "@/hooks/useCustomerAuth";
import { RegionProvider } from "@/hooks/RegionProvider";
import CustomerLogin from "@/pages/customer/CustomerLogin";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import ForgotPassword from "@/pages/customer/ForgotPassword";
import ResetPassword from "@/pages/customer/ResetPassword";
import VerifyEmail from "@/pages/customer/VerifyEmail";
import CustomerSupport from "@/pages/customer/CustomerSupport";
import CustomerSupportDetail from "@/pages/customer/CustomerSupportDetail";
import CustomerWebsiteBilling from "@/pages/customer/CustomerWebsiteBilling";
import SiteOverview from "@/pages/customer/site/SiteOverview";
import SiteAgenda from "@/pages/customer/site/SiteAgenda";
import SiteCustomers from "@/pages/customer/site/SiteCustomers";
import SiteAnalytics from "@/pages/customer/site/SiteAnalytics";
import SiteReminders from "@/pages/customer/site/SiteReminders";
import SiteTaxCalendar from "@/pages/customer/site/SiteTaxCalendar";
import SiteInventory from "@/pages/customer/site/SiteInventory";
import SiteOrders from "@/pages/customer/site/SiteOrders";
import SiteOrderDetail from "@/pages/customer/site/SiteOrderDetail";
import SiteShipping from "@/pages/customer/site/SiteShipping";
import StoreCatalog from "@/pages/store/StoreCatalog";
import StoreProductDetail from "@/pages/store/StoreProductDetail";
import StoreCart from "@/pages/store/StoreCart";
import StoreCheckout from "@/pages/store/StoreCheckout";
import StoreOrderConfirmation from "@/pages/store/StoreOrderConfirmation";
import { AdminHandoverList, AdminHandoverDetail } from "@/pages/AdminHandover";
import AdminMonitoring from "@/pages/AdminMonitoring";
import RepairCanaryPage from "@/dev/RepairCanaryPage";
import BillingSuccess from "@/pages/customer/BillingSuccess";
import BillingCancelled from "@/pages/customer/BillingCancelled";
import PaymentDetails from "@/pages/customer/PaymentDetails";
import CustomerInvoiceDetail from "@/pages/customer/CustomerInvoiceDetail";
import BusinessAccuracy from "@/pages/BusinessAccuracy";
import Privacy from "@/pages/legal/Privacy";
import Cookie from "@/pages/legal/Cookie";
import Terms from "@/pages/legal/Terms";
import ClearTerms from "@/pages/legal/ClearTerms";
import BusinessInformation from "@/pages/legal/BusinessInformation";
import RefundCancellationPolicy from "@/pages/legal/RefundCancellationPolicy";
import { WhatWeDo, WhatWeDontDo } from "@/pages/legal/Scope";
import CovoSite from "@/covo/CovoSite";
import HelenSite from "@/helen/HelenSite";
import TyPoHuatSite from "@/typohuat/TyPoHuatSite";
import MayfairSite from "@/mayfair/MayfairSite";
import RbJewelrySite from "@/rbjewelry/RbJewelrySite";
import CebugoldSite from "@/cebugold/CebugoldSite";
import MyrnaSite from "@/myrna/MyrnaSite";
import AgenciabelenSite from "@/agenciabelen/AgenciabelenSite";
import PqjewelriesSite from "@/pqjewelries/PqjewelriesSite";
import BelcinaSite from "@/belcina/BelcinaSite";
import JehannahSite from "@/jehannah/JehannahSite";
import TajanlangitConcept from "@/construction/tajanlangit/TajanlangitConcept";
import MegwardConcept from "@/construction/megward/MegwardConcept";
import UymatiaoConcept from "@/construction/uymatiao/UymatiaoConcept";
import AlexisConcept from "@/construction/alexis/AlexisConcept";
import JraConcept from "@/construction/jra/JraConcept";
import KElmanConcept from "@/construction/kelman/KElmanConcept";
import RkdPlusConcept from "@/construction/rkdplus/RkdPlusConcept";
import AlgonConcept from "@/construction/algon/AlgonConcept";
import KhanKonChiConcept from "@/construction/khankonchi/KhanKonChiConcept";
import EgiConcept from "@/construction/egi/EgiConcept";
import AcRiveroConcept from "@/construction/acrivero/AcRiveroConcept";
import ConstantinConcept from "@/construction/constantin/ConstantinConcept";
import TuansonConcept from "@/construction/tuanson/TuansonConcept";
import CubeworxConcept from "@/construction/cubeworx/CubeworxConcept";
import DpxConcept from "@/construction/dpx/DpxConcept";
import DentalSite from "@/dental/DentalSite";
import BlacanoConcept from "@/dental/blacano-dental-concept/BlacanoConcept";
import GallineroConcept from "@/dental/gallinero-dental-concept/GallineroConcept";
import BaroquinaConcept from "@/dental/baroquina-dental-concept/BaroquinaConcept";
import BeyondSmileConcept from "@/dental/beyond-smile-dental-concept/BeyondSmileConcept";
import CfConcept from "@/dental/cf-dental-concept/CfConcept";
import CedroConcept from "@/dental/cedro-dental-concept/CedroConcept";
import CitySmileConcept from "@/dental/city-smile-dental-concept/CitySmileConcept";
import ToothDoctorsConcept from "@/dental/tooth-doctors-dental-concept/ToothDoctorsConcept";
import FamilyTreeConcept from "@/dental/family-tree-dental-concept/FamilyTreeConcept";
import SmilesConcept from "@/dental/smiles-dental-concept/SmilesConcept";
import StMichaelsConcept from "@/dental/st-michaels-dental-concept/StMichaelsConcept";
import TwentyOneConcept from "@/dental/21-dental-clinic-concept/TwentyOneConcept";
import OngConcept from "@/dental/ong-dental-concept/OngConcept";
import OrevillosConcept from "@/dental/orevillos-dental-concept/OrevillosConcept";
import { CLINIC_LIST } from "@/dental/dentalClinics";
import LuchelleAnnCabigonSite from "@/realestate/luchelle-ann-cabigon/LuchelleAnnCabigonSite";
import NikkiDelaPenaSite from "@/realestate/nikki-dela-pena/NikkiDelaPenaSite";
import DerrickDumayaSite from "@/realestate/derrick-dumaya/DerrickDumayaSite";
import KayeArellanoSite from "@/realestate/kaye-arellano/KayeArellanoSite";
import JohnPaulSilvaSite from "@/realestate/john-paul-silva/JohnPaulSilvaSite";
import SpotlightSite from "@/events/spotlight/SpotlightSite";
import ConceptsIdeasSite from "@/events/concepts-ideas/ConceptsIdeasSite";
import CenterpieceSite from "@/events/centerpiece/CenterpieceSite";
import HistoriaSite from "@/events/historia/HistoriaSite";
import BaseSite from "@/events/base/BaseSite";
import HeartStarSite from "@/events/heart-star/HeartStarSite";
import YobhellesSite from "@/events/yobhelles/YobhellesSite";
import TcRentalSite from "@/events/tc-rental/TcRentalSite";
import JeffieSite from "@/events/jeffie/JeffieSite";
import MavSite from "@/events/mav/MavSite";
import BerguiaLawSite from "@/law/berguia/BerguiaLawSite";
import ScaleDocumentationSite from "@/law/scale/ScaleDocumentationSite";
import MekingLawSite from "@/law/meking/MekingLawSite";
import GutierrezLegalTeamSite from "@/law/gutierrez/GutierrezLegalTeamSite";
import TlcLawSite from "@/law/tlc/TlcLawSite";
import EspirituLawSite from "@/law/espiritu/EspirituLawSite";
import TrenasHome from "@/law/trenas/TrenasHome";
import TrenasHistory from "@/law/trenas/TrenasHistory";
import TrenasLawyers from "@/law/trenas/TrenasLawyers";
import TrenasPracticeAreas from "@/law/trenas/TrenasPracticeAreas";
import TrenasContact from "@/law/trenas/TrenasContact";
import AboHome from "@/law/abo/AboHome";
import AboAbout from "@/law/abo/AboAbout";
import AboTeam from "@/law/abo/AboTeam";
import AboPracticeAreas from "@/law/abo/AboPracticeAreas";
import AboContact from "@/law/abo/AboContact";
import KvpHome from "@/law/kvp/KvpHome";
import KvpPracticeAreas from "@/law/kvp/KvpPracticeAreas";
import KvpLawyers from "@/law/kvp/KvpLawyers";
import KvpOverseasClients from "@/law/kvp/KvpOverseasClients";
import KvpContact from "@/law/kvp/KvpContact";
import CpaTaxSite from "@/accounting/cpaTax/CpaTaxSite";
import JudyCabreraSite from "@/accounting/judyCabrera/JudyCabreraSite";
import TalabaSite from "@/accounting/talaba/TalabaSite";
import HicoJandaSite from "@/accounting/hicoJanda/HicoJandaSite";
import PsoHome from "@/accounting/pso/PsoHome";
import PsoAbout from "@/accounting/pso/PsoAbout";
import PsoServices from "@/accounting/pso/PsoServices";
import PsoLocations from "@/accounting/pso/PsoLocations";
import PsoContact from "@/accounting/pso/PsoContact";

const BESPOKE_DENTAL_SLUGS = [
  "blacano-dental-concept",
  "gallinero-dental-concept",
  "baroquina-dental-concept",
  "beyond-smile-dental-concept",
  "cf-dental-concept",
  "cedro-dental-concept",
  "city-smile-dental-concept",
  "tooth-doctors-dental-concept",
  "family-tree-dental-concept",
  "smiles-dental-concept",
  "st-michaels-dental-concept",
  "21-dental-clinic-concept",
  "ong-dental-concept",
  "orevillos-dental-concept",
];

function NotFound() {
  return (
    <div data-testid="not-found" className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <h1 className="text-6xl font-bold font-display text-[#0F172A]">404</h1>
      <p className="mt-4 text-lg text-[#475569]">This page could not be found.</p>
      <Link to="/" className="mt-8 inline-flex rounded-xl bg-[#10B981] px-6 py-3 text-sm font-semibold text-white hover:bg-[#059669] transition-colors">
        Back to Home
      </Link>
    </div>
  );
}

// MySiteCare layout (navbar + footer + cookie consent). The COVO site renders
// OUTSIDE this layout so it stays fully standalone / separable.
function MySiteCareLayout() {
  return (
    <>
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <CookieConsent />
    </>
  );
}

// Mounted once, at the very top of the app tree — see lib/monitoring.js's module
// docstring for exactly why this must never be pulled into the standalone Website-
// Ownership handoff package (it isn't; that export doesn't carry App.js's own
// bootstrap code at all). Fails silently if REACT_APP_BACKEND_URL isn't configured.
function MonitoringInit() {
  useEffect(() => {
    if (backendConfigured) {
      initMonitoring({ apiBase: API });
    }
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <AdminAuthProvider>
      <CustomerAuthProvider>
      <BrowserRouter>
        <RegionProvider>
        <MonitoringInit />
        <ScrollToTop />
        <RegionRouteSync />
        <LanguageSwitcher />
        <BusinessSeo />
        <BusinessMediaGallery />
        <BusinessReviewHub />
        <BusinessQualityLayer />
        <ConceptLifecycleGate />
        <CustomerWebsiteAccessLayer />
        <AdSenseScriptLoader />
        <AccessibilityEnhancements />
        <Routes>
          {/* Standalone COVO 24K GOLD HK website (self-contained, exportable) */}
          <Route path="/covo-24k-gold-hk" element={<CovoSite />} />
          {/* Legacy route kept so any earlier links still resolve */}
          <Route path="/COVO-48k-gold-hk" element={<CovoSite />} />
          <Route path="/qa/mysitecare-qa-website-1" element={<CovoSite />} />
          <Route path="/qa/mysitecare-qa-website-2" element={<BerguiaLawSite />} />
          <Route path="/qa/mysitecare-qa-website-3" element={<MegwardConcept />} />

          {/* Standalone jewelry prospect concept websites (self-contained, exportable) */}
          <Route path="/helen-jewelry-shop-concept" element={<HelenSite />} />
          <Route path="/ty-po-huat-goldsmith-concept" element={<TyPoHuatSite />} />
          <Route path="/mayfair-goldsmith-concept" element={<MayfairSite />} />
          <Route path="/rb-jewelry-concept" element={<RbJewelrySite />} />
          <Route path="/cebu-gold-pacific-concept" element={<CebugoldSite />} />
          <Route path="/myrnas-jewelry-concept" element={<MyrnaSite />} />
          <Route path="/agencia-belen-pawnshop-concept" element={<AgenciabelenSite />} />
          <Route path="/pq-jewelries-concept" element={<PqjewelriesSite />} />
          <Route path="/belcina-jewelry-concept" element={<BelcinaSite />} />
          <Route path="/jehannah-goldsmith-concept" element={<JehannahSite />} />

          {/* Construction concept sites (bespoke per company, own vertical UX) */}
          <Route path="/ac-tajanlangit-construction-concept" element={<TajanlangitConcept />} />
          <Route path="/megward-construction-concept" element={<MegwardConcept />} />
          <Route path="/g-uymatiao-jr-construction-concept" element={<UymatiaoConcept />} />
          <Route path="/alexis-construction-concept" element={<AlexisConcept />} />
          <Route path="/jra-home-builders-concept" element={<JraConcept />} />
          <Route path="/k-elman-construction-concept" element={<KElmanConcept />} />
          <Route path="/rkd-plus-construction-concept" element={<RkdPlusConcept />} />
          <Route path="/algon-engineering-construction-concept" element={<AlgonConcept />} />
          <Route path="/khan-kon-chi-construction-concept" element={<KhanKonChiConcept />} />
          <Route path="/egi-construction-concept" element={<EgiConcept />} />
          <Route path="/ac-rivero-development-concept" element={<AcRiveroConcept />} />
          <Route path="/constantin-design-build-concept" element={<ConstantinConcept />} />
          <Route path="/tuanson-construction-concept" element={<TuansonConcept />} />
          <Route path="/cubeworx-builders-concept" element={<CubeworxConcept />} />
          <Route path="/dpx-negros-construction-concept" element={<DpxConcept />} />

          {/* Bespoke redesigned dental concept sites (dedicated components, approved new standard) */}
          <Route path="/blacano-dental-concept" element={<BlacanoConcept />} />
          <Route path="/gallinero-dental-concept" element={<GallineroConcept />} />
          <Route path="/baroquina-dental-concept" element={<BaroquinaConcept />} />
          <Route path="/beyond-smile-dental-concept" element={<BeyondSmileConcept />} />
          <Route path="/cf-dental-concept" element={<CfConcept />} />
          <Route path="/cedro-dental-concept" element={<CedroConcept />} />
          <Route path="/city-smile-dental-concept" element={<CitySmileConcept />} />
          <Route path="/tooth-doctors-dental-concept" element={<ToothDoctorsConcept />} />
          <Route path="/family-tree-dental-concept" element={<FamilyTreeConcept />} />
          <Route path="/smiles-dental-concept" element={<SmilesConcept />} />
          <Route path="/st-michaels-dental-concept" element={<StMichaelsConcept />} />
          <Route path="/21-dental-clinic-concept" element={<TwentyOneConcept />} />
          <Route path="/ong-dental-concept" element={<OngConcept />} />
          <Route path="/orevillos-dental-concept" element={<OrevillosConcept />} />

          {/* Standalone Dental Clinic concept websites (shared renderer, self-contained per slug) */}
          {CLINIC_LIST.filter(({ slug }) => !BESPOKE_DENTAL_SLUGS.includes(slug)).map(({ slug }) => (
            <Route key={slug} path={`/${slug}`} element={<DentalSite slug={slug} />} />
          ))}

          {/* Research-backed real-estate broker concepts; each has its own self-contained folder (src/realestate/{slug}). */}
          <Route path="/luchelle-ann-cabigon-real-estate-concept" element={<LuchelleAnnCabigonSite />} />
          <Route path="/nikki-dela-pena-real-estate-concept" element={<NikkiDelaPenaSite />} />
          <Route path="/derrick-dumaya-real-estate-concept" element={<DerrickDumayaSite />} />
          <Route path="/kaye-arellano-real-estate-concept" element={<KayeArellanoSite />} />
          <Route path="/john-paul-silva-real-estate-concept" element={<JohnPaulSilvaSite />} />

          {/* Wedding, event, venue and rental concepts; each has its own self-contained folder (src/events/{slug}). */}
          <Route path="/spotlight-events-ph-concept" element={<SpotlightSite />} />
          <Route path="/concepts-ideas-events-cebu-concept" element={<ConceptsIdeasSite />} />
          <Route path="/the-centerpiece-cebu-concept" element={<CenterpieceSite />} />
          <Route path="/historia-events-ph-concept" element={<HistoriaSite />} />
          <Route path="/base-function-hall-concept" element={<BaseSite />} />
          <Route path="/heart-star-events-place-concept" element={<HeartStarSite />} />
          <Route path="/yobhelles-meetings-banquets-concept" element={<YobhellesSite />} />
          <Route path="/tc-rental-event-services-concept" element={<TcRentalSite />} />
          <Route path="/jeffie-party-shop-concept" element={<JeffieSite />} />
          <Route path="/mav-party-needs-services-concept" element={<MavSite />} />

          {/* Law & accounting concept sites (bespoke per firm, own vertical UX) */}
          <Route path="/berguia-law-davao-concept" element={<BerguiaLawSite />} />
          <Route path="/scale-documentation-makati-concept" element={<ScaleDocumentationSite />} />
          <Route path="/meking-law-firm-concept" element={<MekingLawSite />} />
          <Route path="/gutierrez-legal-team-concept" element={<GutierrezLegalTeamSite />} />
          <Route path="/tlc-law-firm-mandaue-concept" element={<TlcLawSite />} />
          <Route path="/espiritu-associates-law-bacolod-concept" element={<EspirituLawSite />} />

          {/* Treñas Law — multi-page firm (real routed pages, shared TrenasLayout chrome) */}
          <Route path="/trenas-law-iloilo-concept" element={<TrenasHome />} />
          <Route path="/trenas-law-iloilo-concept/history" element={<TrenasHistory />} />
          <Route path="/trenas-law-iloilo-concept/lawyers" element={<TrenasLawyers />} />
          <Route path="/trenas-law-iloilo-concept/practice-areas" element={<TrenasPracticeAreas />} />
          <Route path="/trenas-law-iloilo-concept/contact" element={<TrenasContact />} />

          {/* Abo & Penaranda — multi-page firm (real routed pages, shared AboLayout chrome) */}
          <Route path="/abo-penaranda-law-bgc-concept" element={<AboHome />} />
          <Route path="/abo-penaranda-law-bgc-concept/about" element={<AboAbout />} />
          <Route path="/abo-penaranda-law-bgc-concept/team" element={<AboTeam />} />
          <Route path="/abo-penaranda-law-bgc-concept/practice-areas" element={<AboPracticeAreas />} />
          <Route path="/abo-penaranda-law-bgc-concept/contact" element={<AboContact />} />

          {/* KVP Cabahug Law — multi-page firm (real routed pages, shared KvpLayout chrome) */}
          <Route path="/kvp-cabahug-law-cebu-concept" element={<KvpHome />} />
          <Route path="/kvp-cabahug-law-cebu-concept/practice-areas" element={<KvpPracticeAreas />} />
          <Route path="/kvp-cabahug-law-cebu-concept/lawyers" element={<KvpLawyers />} />
          <Route path="/kvp-cabahug-law-cebu-concept/overseas-clients" element={<KvpOverseasClients />} />
          <Route path="/kvp-cabahug-law-cebu-concept/contact" element={<KvpContact />} />

          {/* Accounting concept sites (bespoke per firm, own vertical UX) */}
          <Route path="/cpa-tax-bookkeeping-mandaluyong-concept" element={<CpaTaxSite />} />
          <Route path="/judy-cabrera-accounting-concept" element={<JudyCabreraSite />} />
          <Route path="/talaba-accounting-cebu-concept" element={<TalabaSite />} />
          <Route path="/hico-janda-cpas-concept" element={<HicoJandaSite />} />

          {/* PSO Accounting Group — multi-page firm (real routed pages, shared PsoLayout chrome) */}
          <Route path="/pso-accounting-group-concept" element={<PsoHome />} />
          <Route path="/pso-accounting-group-concept/about" element={<PsoAbout />} />
          <Route path="/pso-accounting-group-concept/services" element={<PsoServices />} />
          <Route path="/pso-accounting-group-concept/locations" element={<PsoLocations />} />
          <Route path="/pso-accounting-group-concept/contact" element={<PsoContact />} />

          <Route path="/admin/business-accuracy" element={<BusinessAccuracy />} />

          {/* MySiteCare site */}
          <Route element={<MySiteCareLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/plans/:slug" element={<PlanDetail />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/concept-correction" element={<ConceptCorrectionRequest />} />
            <Route path="/clear-terms" element={<ClearTerms />} />
            <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicy />} />
            <Route path="/what-we-do" element={<WhatWeDo />} />
            <Route path="/what-we-dont" element={<WhatWeDontDo />} />
            <Route path="/terms-and-conditions" element={<Terms />} />
            <Route path="/business-information" element={<BusinessInformation />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/cookie-policy" element={<Cookie />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/sites" element={<AdminSites />} />
            <Route path="/admin/sites/:slug" element={<AdminSiteDetail />} />
            <Route path="/admin/sales" element={<AdminSales />} />
            <Route path="/admin/sales/:slug" element={<AdminSalesDetail />} />
            <Route path="/admin/sites/:slug/onboarding" element={<AdminOnboarding />} />
            <Route path="/admin/finance" element={<AdminFinance />} />
            <Route path="/admin/compliance" element={<AdminCompliance />} />
            <Route path="/admin/handover" element={<AdminHandoverList />} />
            <Route path="/admin/handover/:slug" element={<AdminHandoverDetail />} />
            <Route path="/admin/monitoring" element={<AdminMonitoring />} />
            <Route path="/dev/repair-canary" element={<RepairCanaryPage />} />

            {/* Customer account portal */}
            <Route path="/register" element={<Register />} />
            <Route path="/account/login" element={<CustomerLogin />} />
            <Route path="/account/forgot-password" element={<ForgotPassword />} />
            <Route path="/account/reset-password" element={<ResetPassword />} />
            <Route path="/account/verify-email" element={<VerifyEmail />} />
            <Route path="/account/dashboard" element={<CustomerDashboard />} />
            <Route path="/account/support" element={<CustomerSupport />} />
            <Route path="/account/support/:ticketId" element={<CustomerSupportDetail />} />
            <Route path="/checkout" element={<PaymentDetails />} />
            <Route path="/account/invoices/:invoiceId" element={<CustomerInvoiceDetail />} />
            <Route path="/account/websites/:slug" element={<CustomerWebsiteBilling />} />
            <Route path="/account/websites/:slug/overview" element={<SiteOverview />} />
            <Route path="/account/websites/:slug/agenda" element={<SiteAgenda />} />
            <Route path="/account/websites/:slug/customers" element={<SiteCustomers />} />
            <Route path="/account/websites/:slug/analytics" element={<SiteAnalytics />} />
            <Route path="/account/websites/:slug/reminders" element={<SiteReminders />} />
            <Route path="/account/websites/:slug/tax-calendar" element={<SiteTaxCalendar />} />
            <Route path="/account/websites/:slug/inventory" element={<SiteInventory />} />
            <Route path="/account/websites/:slug/orders" element={<SiteOrders />} />
            <Route path="/account/websites/:slug/orders/:orderId" element={<SiteOrderDetail />} />
            <Route path="/account/websites/:slug/shipping" element={<SiteShipping />} />
            <Route path="/account/websites/:slug/billing" element={<CustomerWebsiteBilling />} />

            {/* Public storefront — only reachable for commerce-enabled online_client sites (backend-enforced) */}
            <Route path="/store/:slug" element={<StoreCatalog />} />
            <Route path="/store/:slug/products/:productId" element={<StoreProductDetail />} />
            <Route path="/store/:slug/cart" element={<StoreCart />} />
            <Route path="/store/:slug/checkout" element={<StoreCheckout />} />
            <Route path="/store/:slug/order-confirmation/:orderId" element={<StoreOrderConfirmation />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancelled" element={<BillingCancelled />} />

            {/* Philippines region */}
            <Route path="/ph" element={<Home />} />
            <Route path="/ph/about" element={<About />} />
            <Route path="/ph/plans" element={<Plans />} />
            <Route path="/ph/plans/:slug" element={<PlanDetail />} />
            <Route path="/ph/checkout" element={<PaymentDetails />} />
            <Route path="/ph/faq" element={<FAQ />} />
            <Route path="/ph/contact" element={<Contact />} />
            <Route path="/ph/why-this-matters" element={<WhyThisMatters />} />
            <Route path="/ph/clear-terms" element={<ClearTerms />} />
            <Route path="/ph/refund-cancellation-policy" element={<RefundCancellationPolicy />} />
            <Route path="/ph/what-we-do" element={<WhatWeDo />} />
            <Route path="/ph/what-we-dont" element={<WhatWeDontDo />} />
            <Route path="/ph/terms-and-conditions" element={<Terms />} />
            <Route path="/ph/business-information" element={<BusinessInformation />} />
            <Route path="/ph/privacy-policy" element={<Privacy />} />
            <Route path="/ph/cookie-policy" element={<Cookie />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        <SupportBubble />
        </RegionProvider>
      </BrowserRouter>
      </CustomerAuthProvider>
      </AdminAuthProvider>
    </div>
  );
}

export default App;
